'use client'

import SuspendedPostHogPageView from '@/app/(protected)/PosthogPageview'
import posthog from 'posthog-js'
import { PostHogProvider as Provider } from 'posthog-js/react'
import { useEffect } from 'react'
import PostHogIdentify from './PostHogIdentify'
import { getClientEnvVariable } from '@/util/environment-variables'
import { isChunkLoadError } from '@/util/chunk-load-error'

export function PostHogProvider({ children }: { children: React.ReactNode }) {
  useEffect(() => {
    const posthogKey = getClientEnvVariable('NEXT_PUBLIC_POSTHOG_KEY')
    // Only initialize PostHog in production or Staging
    posthog.init(posthogKey, {
      api_host: '/gratiaphog',
      ui_host: 'https://us.posthog.com',
      person_profiles: 'identified_only', // or 'always' to create profiles for anonymous users as well
      // Add these configurations to optimize feature flag requests
      advanced_disable_feature_flags_on_first_load: false, // Enable automatic flag requests on page load
      bootstrap: {
        // preload feature flags here if needed
        distinctID: undefined,
        isIdentifiedID: false,
        featureFlags: {},
      },
      // Drop transient post-deploy stale-chunk failures (the error boundary
      // recovers from these with a reload) so they don't create noisy issues.
      before_send: (event) => {
        if (event?.event === '$exception') {
          const exceptions = event.properties?.$exception_list
          if (
            Array.isArray(exceptions) &&
            exceptions.some((exception) =>
              isChunkLoadError({ name: exception?.type, message: exception?.value }),
            )
          ) {
            return null
          }
        }
        return event
      },
    })

    const hideWidgetWhenReady = window.setInterval(() => {
      if (posthog.conversations?.isAvailable()) {
        posthog.conversations.hide()
        window.clearInterval(hideWidgetWhenReady)
      }
    }, 200)
    const hideWidgetTimeout = window.setTimeout(() => {
      window.clearInterval(hideWidgetWhenReady)
    }, 15_000)

    return () => {
      window.clearInterval(hideWidgetWhenReady)
      window.clearTimeout(hideWidgetTimeout)
    }
  }, [])

  return (
    <Provider client={posthog}>
      <SuspendedPostHogPageView />
      <PostHogIdentify />
      {children}
    </Provider>
  )
}
